import { ScalarSpreadsheetConfigs } from 'common/types/scalarSpreadsheet';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { OPMCalculatorUrlInputs } from 'pages/ValuationsAllocation/hooks/useOPMCalculatorUrlState';
import { dbShortDate } from 'utilities';
import { OPMCalculatorLinkSource, OPMCalculatorLinkSourceCell } from './ShowOPMCalculationsLink.types';
import type { OPMCalculatorDecimalPlaces } from './useOPMCalculatorDecimalPlaces';

// The workbook as `ValuationContext` exposes it: sheets keyed by name. Typed off the base
// spreadsheet types rather than `ValuationsContextValues` to keep this module out of the
// `types.ts` <-> `components` import cycle.
type Workbook = ScalarSpreadsheetConfigs;

/**
 * `SpreadsheetConfig` indexes its cells by cell key and by `customKey`, so both hit directly.
 * Alias is not indexed, hence the scan -- the same `key || alias || customKey` lookup
 * `FutureExitApproach.getCellValue` already does.
 */
const findCell = (workbook: Workbook, sheetName: string, key: string): ICell | undefined => {
  const cells = workbook?.[sheetName]?.cells as Record<string, ICell> | undefined;

  if (!cells) {
    return undefined;
  }

  return cells[key] ?? Object.values(cells).find(cell => cell?.alias === key);
};

/** A finite number from a cell value, or undefined. Cell values are already decimals. */
const readNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const round = (value: number | undefined, decimalPlaces?: number) =>
  (value === undefined || decimalPlaces === undefined ? value : Number(value.toFixed(decimalPlaces)));

/**
 * ISO `YYYY-MM-DD`, or undefined. Grid date cells hold `MM/DD/YYYY` and the API ISO, so both are
 * accepted. Guarded up front: `dbShortDate` of nothing is today, not nothing.
 */
export const toOPMDate = (value: unknown): string | undefined =>
  (value ? dbShortDate(value as string) ?? undefined : undefined);

const readSourceCell = (
  workbook: Workbook,
  source: OPMCalculatorLinkSource,
  sourceCell?: OPMCalculatorLinkSourceCell
) => {
  if (!sourceCell) {
    return undefined;
  }
  return readNumber(findCell(workbook, sourceCell.sheetName ?? source.sheetName, sourceCell.key)?.value);
};

/**
 * Collects the OPM Calculator inputs a link should carry, rounding each number to the precision
 * its `OPMCalculator` column holds (`decimalPlaces`, from the backend field attributes) and the date to ISO. Anything missing is left out rather than sent as NaN, so a
 * partly-filled surface still opens the calculator with what it does have.
 */
const readOPMCalculatorInputs = (
  workbook: Workbook | null | undefined,
  source: OPMCalculatorLinkSource,
  decimalPlaces: OPMCalculatorDecimalPlaces
): OPMCalculatorUrlInputs => {
  if (!workbook) {
    return {};
  }

  const capTableId = source.capTableId ?? readSourceCell(workbook, source, source.cells.capTableId);
  const equityValue = readSourceCell(workbook, source, source.cells.equityValue);
  const riskFreeRate = readSourceCell(workbook, source, source.cells.riskFreeRate);
  const maturity = readSourceCell(workbook, source, source.cells.maturity);
  const volatility = readSourceCell(workbook, source, source.cells.volatility);
  const opmDateCell = source.opmDateCell
    ? findCell(workbook, source.opmDateCell.sheetName ?? source.sheetName, source.opmDateCell.key)
    : undefined;

  return {
    // `useOPMCalculatorUrlState` rejects anything that is not a positive safe integer id.
    capTableId:
      capTableId !== undefined && Number.isSafeInteger(capTableId) && capTableId > 0 ? capTableId : undefined,
    equityValue: round(equityValue, decimalPlaces.equityValue),
    riskFreeRate: round(riskFreeRate, decimalPlaces.riskFreeRate),
    maturity: round(maturity, decimalPlaces.maturity),
    volatility: round(volatility, decimalPlaces.volatility),
    opmDate: toOPMDate(opmDateCell?.value) ?? toOPMDate(source.opmDate),
  };
};

export default readOPMCalculatorInputs;
